import { Box, Heading, Icon, Text } from '@medely/ui-kit';
import React from 'react';
import { JobDetailsListItem } from '../../jobs';
import { displayParking } from '@mp/utils';
import { AssignmentForAssignmentDetailsGql } from '../graphql';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';

type AssignmentOnsiteLogisticsProps = {
  smallIcon?: boolean;
  assignment?: FragmentType<typeof AssignmentForAssignmentDetailsGql> | null;
};

export const AssignmentOnsiteLogistics = ({
  smallIcon,
  assignment: inputAssignment,
}: AssignmentOnsiteLogisticsProps) => {
  const assignment = parseFragmentData(AssignmentForAssignmentDetailsGql, inputAssignment);

  const bookedOrInProgress = ['booked', 'in_progress'].includes(assignment?.status ?? '');

  const scrubsInfo = assignment?.scrubs_provided
    ? 'The facility will provide scrubs'
    : 'Professionals should bring their own scrubs';

  const scrubsColors = () => {
    const scrubColors = assignment?.primary_location?.scrub_colors;
    return scrubColors ? scrubColors.map((color) => color).join(', ') : null;
  };

  const parking = displayParking(
    assignment?.primary_location?.parking_free,
    assignment?.primary_location?.parking_reimbursed,
    assignment?.primary_location?.parking_validated,
    assignment?.primary_location?.parking_cost_cents,
  );
  const parkingInstructions = assignment?.primary_location?.parking_instructions;

  const clockInInstructions = assignment?.primary_location?.additional_check_in_instructions;

  const isUnpaidMealBreak = assignment?.track_unpaid_breaks;
  const mealBreakText = isUnpaidMealBreak ? 'Unpaid meal breaks' : 'Paid meal breaks';
  const trackedMealBreakTime = assignment?.estimated_unpaid_break_time_minutes;
  const mealBreakDetails = assignment?.primary_location?.meal_break_details;

  return (
    <Box testId="assignment-onsite-logistics" pb={2}>
      <Box py={2}>
        <Heading size="s" color="text.primary" testId="assignment-onsite-logistics-title">
          Onsite logistics
        </Heading>
      </Box>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-tshirt"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title="Dress code"
        >
          <Text size="m" color="text.secondary">{`• ${scrubsInfo}`}</Text>
          {scrubsColors() && (
            <Text size="m" color="text.secondary">{`• Acceptable colors: ${scrubsColors()}`}</Text>
          )}
        </JobDetailsListItem>
      </Box>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-parking-circle"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title="Parking"
        >
          <Text size="m" color="text.secondary">{`• ${parking}`}</Text>
          {parkingInstructions && (
            <Text size="m" color="text.secondary">{`• ${parkingInstructions}`}</Text>
          )}
        </JobDetailsListItem>
      </Box>
      {bookedOrInProgress && clockInInstructions && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-clock"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Clock in instructions"
          >
            <Text size="m" color="text.secondary">{`• ${clockInInstructions}`}</Text>
          </JobDetailsListItem>
        </Box>
      )}
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-burger-soda"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title="Meal breaks"
        >
          {Boolean(trackedMealBreakTime) && (
            <Text size="m" color="text.secondary">{`• Duration: ${trackedMealBreakTime} min`}</Text>
          )}
          <Text size="m" color="text.secondary">{`• ${mealBreakText}`}</Text>
          {mealBreakDetails && (
            <Text size="m" color="text.secondary">{`• ${mealBreakDetails}`}</Text>
          )}
        </JobDetailsListItem>
      </Box>
    </Box>
  );
};
